import { useState } from 'react'
import { toast } from 'react-hot-toast'
import { Plus, Pencil, Trash2, Building2 } from 'lucide-react'
import {
  Card,
  CardContent,
  Button,
  Input,
  Label,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@jira-clone/ui'
import { useDepartments, useDepartmentMutations, type DepartmentOption } from '../../shared/hooks/useDepartments'
import { getApiErrorMessage } from '../../shared/utils/apiError'

const DEFAULT_COLOR = '#6366f1'

interface DraftState {
  id?: string
  name: string
  description: string
  color: string
}

const EMPTY_DRAFT: DraftState = { name: '', description: '', color: DEFAULT_COLOR }

export default function DepartmentsAdminPage() {
  const { data: departments = [], isLoading } = useDepartments()
  const { create, update, remove } = useDepartmentMutations()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [draft, setDraft] = useState<DraftState>(EMPTY_DRAFT)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const openCreate = () => {
    setDraft(EMPTY_DRAFT)
    setDialogOpen(true)
  }

  const openEdit = (dept: DepartmentOption) => {
    setDraft({ id: dept.id, name: dept.name, description: dept.description || '', color: dept.color || DEFAULT_COLOR })
    setDialogOpen(true)
  }

  const handleSave = async () => {
    if (!draft.name.trim()) {
      toast.error('Department name is required.')
      return
    }
    try {
      if (draft.id) {
        await update.mutateAsync({
          id: draft.id,
          payload: { name: draft.name.trim(), description: draft.description.trim() || undefined, color: draft.color },
        })
        toast.success('Department updated.')
      } else {
        await create.mutateAsync({
          name: draft.name.trim(),
          description: draft.description.trim() || undefined,
          color: draft.color,
        })
        toast.success('Department created.')
      }
      setDialogOpen(false)
    } catch (err) {
      toast.error(getApiErrorMessage(err, draft.id ? 'update this department' : 'create this department'))
    }
  }

  const handleDelete = async (dept: DepartmentOption) => {
    if (!confirm(`Delete "${dept.name}"? This can't be undone.`)) return
    setDeletingId(dept.id)
    try {
      await remove.mutateAsync(dept.id)
      toast.success('Department deleted.')
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'delete this department'))
    } finally {
      setDeletingId(null)
    }
  }

  return (
    <div className="space-y-4 max-w-3xl">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-foreground">Departments</h2>
          <p className="text-sm text-muted-foreground">Manage the departments people can be grouped under.</p>
        </div>
        <Button onClick={openCreate} className="gap-1.5">
          <Plus className="h-4 w-4" /> New Department
        </Button>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading departments...</p>
      ) : departments.length === 0 ? (
        <p className="text-sm text-muted-foreground">No departments yet. Create the first one.</p>
      ) : (
        <div className="space-y-2">
          {departments.map((dept) => (
            <Card key={dept.id}>
              <CardContent className="p-4 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <span
                    className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0"
                    style={{ backgroundColor: `${dept.color || DEFAULT_COLOR}20`, color: dept.color || DEFAULT_COLOR }}
                  >
                    <Building2 className="h-4 w-4" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-foreground truncate">{dept.name}</p>
                    {dept.description && (
                      <p className="text-xs text-muted-foreground truncate">{dept.description}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(dept)} title="Edit">
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(dept)}
                    disabled={deletingId === dept.id}
                    title="Delete"
                  >
                    <Trash2 className="h-3.5 w-3.5 text-destructive" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{draft.id ? 'Edit Department' : 'New Department'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-1.5">
              <Label htmlFor="dept-name">Name</Label>
              <Input
                id="dept-name"
                value={draft.name}
                onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                placeholder="e.g. Customer Success"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="dept-description">Description (optional)</Label>
              <Input
                id="dept-description"
                value={draft.description}
                onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
                placeholder="What this department does"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="dept-color">Color</Label>
              <div className="flex items-center gap-2">
                <input
                  id="dept-color"
                  type="color"
                  value={draft.color}
                  onChange={(e) => setDraft((d) => ({ ...d, color: e.target.value }))}
                  className="h-9 w-12 rounded border border-border cursor-pointer"
                />
                <span className="text-xs text-muted-foreground font-mono">{draft.color}</span>
              </div>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={create.isPending || update.isPending}>
              {draft.id ? 'Save Changes' : 'Create Department'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
